import React from "react";
import { TouchableOpacity } from "react-native";
import { injectIntl } from "react-intl";
import styled, { withTheme } from "styled-components/native";
import { localize } from "cci-utility";
import Icon from "../icon/FontAwesomePro";
import { layoutCss } from "../layout/common";

/**
 * This little piece of helper magic is in charge of getting the right style attribute based on:
 * 1. If the component is disabled or not
 * 2. If the ui prop was specified and the attribute exists for that ui
 * 3. If none of the above exists, we use the default fallback.
 * 
 * An example of this might be this object:
 * 
 *   button: {
 *     primary: {
 *       borderRadius: "30px",
 *       border: "2px solid #ff985b",
 *       iconSize: 15,
 *       padding: "10px",
 *       disabled: {
 *         border: "2px solid red"
 *       }
 *     },
 * 
 *     link: {
 *       iconSize: 15
 *     }
 *   }
 * 
 */
const buttonStyle = (props, name, defaultValue) => {
  const theme = props.theme,
    base = theme.button[props.ui];

  return (
    (props.disabled === true
      ? (base.disabled && base.disabled[name]) || base[name] || theme[name]
      : props[name] || base[name] || theme[name]) || defaultValue
  );
};

/* Touchable wrapper that also handles the layout of the button via our helper function. */
const Wrapper = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${props => buttonStyle(props, "padding", 0)};
  margin: ${props => buttonStyle(props, "margin", 0)};
  background-color: ${props =>
    buttonStyle(props, "backgroundColor", "transparent")};
  border: ${props => buttonStyle(props, "border", 0)};
  border-radius: ${props => buttonStyle(props, "borderRadius", 0)};

  ${layoutCss}
`;

/* Button text component, used only if a title prop was passed. */
const Label = styled.Text`
  font-family: ${props => buttonStyle(props, "fontFamily")};
  font-size: ${props => buttonStyle(props, "fontSize", props.fontSize || 12)}px;
  letter-spacing: ${props =>
    buttonStyle(props, "letterSpacing", props.letterSpacing || 0)}px;
  color: ${props => buttonStyle(props, "textColor", props.theme.fontColor)};
  text-transform: ${props => buttonStyle(props, "textTransform", "none")};
  text-decoration-line: ${props => buttonStyle(props, "textDecorationLine", "none")};
`;

/* Icon component that goes to the left or the right of the button title */
const ButtonIcon = props => {
  const style = {
    ["margin" + props.marginName]: props.title ? 10 : 0,
    textShadowColor: buttonStyle(props, "textShadowColor", "transparent"),
    textShadowOffset: buttonStyle(props, "textShadowOffset", {
      width: 0,
      height: 0
    }),
    textShadowRadius: buttonStyle(props, "textShadowRadius", 0),
    shadowColor: buttonStyle(props, "shadowColor", "transparent"),
    shadowOffset: buttonStyle(props, "shadowOffset", { width: 0, height: 0 }),
    shadowOpacity: buttonStyle(props, "shadowOpacity", 0),
    shadowRadius: buttonStyle(props, "shadowRadius", 0)
  };

  return (
    <Icon
      name={props.name}
      weight={props.weight}
      color={props.fontColor || buttonStyle(props, "fontColor")}
      iconSize={buttonStyle(props, "iconSize")}
      style={style}
    />
  );
};

const Button = props => {
  const {
      intl,
      disabled,
      fontColor,
      fontSize,
      letterSpacing,
      leftIcon,
      leftIconWeight,
      rightIcon,
      rightIconWeight,
      intlValues
    } = props,
    title = localize(intl, props.title, intlValues),
    color = fontColor || buttonStyle(props, "fontColor");

  return (
    <Wrapper {...props}>
      {/* Left Icon */}
      {leftIcon && (
        <ButtonIcon
          {...props}
          name={leftIcon}
          fontColor={color}
          marginName="Right"
          weight={leftIconWeight}
        />
      )}

      {/* Text */}
      {title && (
        <Label
          ui={props.ui}
          fontSize={fontSize}
          letterSpacing={letterSpacing}
          selectable={false}
          style={{ color }}
        >
          {title}
        </Label>
      )}

      {/* Right Icon */}
      {rightIcon && (
        <ButtonIcon
          {...props}
          name={rightIcon}
          fontColor={color}
          marginName="Left"
          weight={rightIconWeight}
        />
      )}
    </Wrapper>
  );
};

Button.defaultProps = {
  ui: "primary"
};

export default injectIntl(withTheme(Button));
